// External libraries
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Internal modules
import { logger } from '../logger';
import { getWeasleyEligibility } from '../weasleyEligibility';
import { redirectToXoon } from '../utils/redirects';
import { getLocale } from '../globals';
import { queryInitialData } from '@/components/Locale/Country/shared/graphql/operations';
import type { NavigateToBillingPageCallback } from '@/lib/reactHooks/types';

export const useNavigateToBillingPage = (): NavigateToBillingPageCallback => {
  const { country, language } = getLocale();
  const navigate = useNavigate();

  const navigateToBillingPage = useCallback(async ({ reason } = {}) => {
    logger.info(`navigate_to_billing_triggered_${reason}`);
    try {
      const eligibilityResponse = await getWeasleyEligibility();

      if (eligibilityResponse?.eligible) {
        // If eligible preload the billing component before making any
        // API calls
        import('@/components/Billing/Billing');
        await queryInitialData({
          countryCode: country,
          // @ts-expect-error type GlobalLocality
          languageCode: language,
          resetStoreOverridesKey: 'landingToBilling',
        });
        logger.info('navigate_to_billing_weasley_eligible');
        // TODO: this for ramp analysis. Can be deleted once
        // VIP page is stable
        // Flush all VIP page logs before loading billing page
        logger.flush();
        navigate({
          pathname: '/checkoutweb/signup',
          search: window.location.search,
        });
      } else {
        logger.info('redirect_to_xoon_weasley_ineligible');
        redirectToXoon({
          params: { fromVIPPage: 'billing' },
          reason: 'billing_ineligible_on_landing_page',
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        const { message, stack } = error;
        logger.warn('redirect_to_xoon_error_on_navigate', { message, stack });
      } else {
        logger.warn('redirect_to_xoon_error_on_navigate');
      }
      redirectToXoon({
        params: { fromVIPPage: 'billing' },
        reason: 'billing_eligibility_check_error_on_landing_page',
      });
    }
  }, []);

  return navigateToBillingPage;
};
